import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo, teamMembers, testimonials } from '@/mocks/data';
import { AboutPageSchema, BreadcrumbListSchema, WebPageSchema } from '@/components/base/SchemaOrg';

const teamImages: Record<string, string> = {
  'team-01': 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20psychologist%20portrait%2C%20warm%20gentle%20expression%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20professional%20clean%20style%2C%20trustworthy%20appearance&width=400&height=400&seq=team-01-avatar&orientation=squarish',
  'team-02': 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20psychologist%20portrait%2C%20friendly%20approachable%20smile%2C%20soft%20studio%20lighting%2C%20warm%20beige%20background%2C%20professional%20clean%20style%2C%20academic%20appearance&width=400&height=400&seq=team-02-avatar&orientation=squarish',
  'team-03': 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20therapist%20portrait%2C%20kind%20caring%20expression%2C%20soft%20natural%20light%2C%20cream%20background%2C%20professional%20yet%20warm%20style%2C%20approachable%20look&width=400&height=400&seq=team-03-avatar&orientation=squarish',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9s8mbknlsngrm4lmi7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const baseUrl = 'https://www.maternal-psych-guide.cn';

  return (
    <>
      <ScrollToTop />
      <AboutPageSchema />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: baseUrl },
          { name: '关于我们', url: `${baseUrl}/about` },
        ]}
      />
      <WebPageSchema
        title="关于我们 - 母子关系心理健康指南"
        description="了解母子关系心理健康指南的品牌故事、专业团队和使命愿景。我们致力于以专业心理学知识守护每一个家庭的亲子关系健康。"
        url={`${baseUrl}/about`}
      />

      <Navbar />

      <main>
        {/* Page Header */}
        <section className="bg-background-100 pt-24 pb-12">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-4 mb-3">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-500">
              了解我们的使命、团队与价值观
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500 text-background-50">
                    <i className="ri-heart-pulse-line text-lg"></i>
                  </div>
                  <span className="font-heading text-xl font-bold text-foreground-900">
                    {aboutInfo.brandName}
                  </span>
                </div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {aboutInfo.tagline}
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  {aboutInfo.introduction}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  <strong className="text-foreground-900">我们的使命：</strong>{aboutInfo.mission}
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-2xl overflow-hidden h-[300px] md:h-[380px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20psychology%20office%20space%20with%20soft%20natural%20lighting%2C%20comfortable%20counseling%20chairs%2C%20bookshelf%20with%20psychology%20books%2C%20plants%2C%20clean%20professional%20aesthetic%2C%20cream%20and%20beige%20tones%2C%20healing%20atmosphere&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                    alt="母子关系心理健康指南办公环境"
                    title="母子关系心理健康指南 - 专业心理咨询环境"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-10 text-center">
              我们的核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutInfo.values.map((val, idx) => (
                <div key={idx} className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-700 mx-auto mb-4">
                    <i className={`${['ri-shield-check-line', 'ri-book-open-line', 'ri-lightbulb-line', 'ri-hand-heart-line'][idx]} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-20 bg-primary-500">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {aboutInfo.stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-background-50/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">
              专业团队
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              由持证心理咨询师组成的专家团队
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <div key={member.name} className="text-center">
                  <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden mx-auto mb-4 bg-background-100">
                    <img
                      src={teamImages[member.imageSeq] || teamImages['team-01']}
                      alt={member.name}
                      title={`${member.name} - ${member.title}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{member.name}</h3>
                  <p className="text-xs text-primary-600 font-medium mb-2">{member.title}</p>
                  <p className="text-sm text-foreground-500 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              来自真实用户和专业人士的评价
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="p-6 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                      <span className="text-secondary-700 text-xs font-semibold">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              如有任何问题或咨询需求，欢迎通过以下表单联系我们
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              <div className="form-honeypot-wrapper">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  电子邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入您的电子邮箱"
                />
              </div>

              <div>
                <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  咨询主题
                </label>
                <select
                  id="contact-topic"
                  name="topic"
                  className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                >
                  <option value="">请选择咨询主题</option>
                  <option value="boundary">母子边界模糊</option>
                  <option value="dependency">母子情感依赖</option>
                  <option value="attachment">依恋障碍</option>
                  <option value="mental-health">亲子关系心理健康</option>
                  <option value="intervention">母子关系干预</option>
                  <option value="counseling">家庭心理咨询</option>
                  <option value="other">其他问题</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  咨询内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请描述您想咨询的问题（限500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {formStatus === 'error' && formError && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                  {formError}
                </div>
              )}

              {formStatus === 'success' && (
                <div className="p-4 rounded-xl bg-accent-50 border border-accent-200 text-sm text-accent-800">
                  提交成功！感谢您的咨询，我们会在1-2个工作日内回复您。
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? '提交中...' : '提交咨询'}
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}